import { FetchHttpTransport } from '@vello/api-client';
import { WebSecureStore, createWebCryptoDeviceSigner, deviceProofProvider } from '@vello/app-shell';
import { createAdminRuntime, type AdminRuntime } from '../src/index';

/** Where the operator console reaches the Vello API. Overridable per environment via `VITE_API_BASE_URL`. */
const DEFAULT_BASE_URL = 'https://api.usevello.app';

/**
 * Compose the real Admin runtime for the browser: the framework-neutral `@vello/admin` logic driven by a real
 * `fetch` transport and a browser-backed secure store. This is the single place the web app touches a platform
 * runtime — every screen consumes the returned `AdminRuntime` and stays testable in Node against injected doubles.
 * The device-key signer (ADR-0039) is a non-extractable WebCrypto P-256 key kept in IndexedDB; it proves possession
 * at login and on every refresh so a copied refresh token cannot renew the session elsewhere.
 */
export function createWebAdminRuntime(): AdminRuntime {
  const env = import.meta.env as { VITE_API_BASE_URL?: string };
  const baseUrl = env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL;
  return createAdminRuntime({
    transport: new FetchHttpTransport({ defaultTimeoutMs: 15_000 }),
    secureStore: new WebSecureStore(),
    baseUrl,
    deviceProof: deviceProofProvider(createWebCryptoDeviceSigner()),
  });
}
